/**
 * Personvern.  2026-10-14 (owner: «the mosque uploads, the Min Masjid operator approves»).
 *
 * The mosque's own privacy policy, as everyone sees it at <the mosque>/personvern: which page is shown now, a new
 * version uploaded, previewed and published, and an earlier one put back. Min Masjid approves the page (per area, in its
 * console); until it has, what the approval allows stays off — so the panel says BEFORE publishing which functions a new
 * page pauses. privacy.manage (admin tier).
 *
 * The page is never put into the panel itself: it is previewed in an iframe with sandbox="" (no scripts, an origin of
 * its own) through srcDoc. The server checks the file with the same rule as the shell and the console (core
 * privacy-page.js); the panel asks that rule first only to answer sooner.
 */
import React, { useState } from 'react';
import { call, errText, post, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { kb, osloTime } from '../lib/format';
import { Btn, Card, DropZone, LoadError, Loading, More, Msg, Tag, TwoStep } from '../components/ui';
import { PrivacyPage } from '../core';
import { pauseWarning } from '../lib/privacy';

const MODE_NO: Record<string, string> = {
  own: 'Moskeens egen side vises.',
  redirect: 'Adressen sender besøkende videre til en annen side.',
  placeholder: 'Ingen egen side er publisert: besøkende ser en nøytral side som sier at erklæringen ikke er publisert her ennå.',
};
const ACTION_NO: Record<string, string> = { publish: 'Publisert', rollback: 'Satt tilbake', adopt: 'Fra før versjonshistorikken' };
const short = (hex: string | null | undefined) => (hex ? hex.slice(0, 12) : '');
/** «01.11» — the day the page went to Min Masjid, in the mosque's (Oslo) time. */
const ddmm = (iso: string) => new Date(iso).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Oslo' });

const labelsOf = (scopes: Body[]): string[] => scopes.flatMap((s: Body) => (s.capabilities || []).map((c: Body) => c.label_no));

/** The page, shown where it can do nothing: no scripts, no same-origin access, no referrer. */
function Preview({ html, title }: { html: string; title: string }) {
  return <iframe className="pv-preview" sandbox="" srcDoc={html} title={title} referrerPolicy="no-referrer" />;
}

interface Draft { html: string; name: string; sha256: string; title: string; bytes: number; pauses: Body[] }

export function Personvern() {
  const { data, error, reload } = useLoad<Body>('/privacy-page');
  const [msg, say] = useMsg();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState<{ id: number; title: string; html: string } | null>(null);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  if (!data.onPlatform) {
    return <Card title="Personvern" hint={data.message_no || 'Personvernsiden for denne installasjonen settes av driftsansvarlig.'} />;
  }

  const choose = async (file: File) => {
    setDraft(null); say('');
    if (file.size > data.maxBytes) return say(`Filen er for stor (${kb(file.size)}). Høyst ${kb(data.maxBytes)}.`, 'err');
    const html = await file.text();
    const local = PrivacyPage.validate(html);
    if (!local.ok) return say(local.message_no || 'Filen kan ikke brukes.', 'err');
    setChecking(true);
    const r = await post('/privacy-page/check', { html });
    setChecking(false);
    if (!r.ok) return say(errText(r.body), 'err');
    if (r.body.unchanged) return say('Denne siden er allerede publisert. Ingenting å gjøre.', 'ok');
    setDraft({ html, name: file.name, sha256: r.body.sha256, title: r.body.title, bytes: r.body.bytes, pauses: r.body.pauses || [] });
  };
  const publish = async () => {
    if (!draft) return;
    setBusy(true);
    const r = await post('/privacy-page/publish', { html: draft.html, sha256: draft.sha256, confirm: true });
    setBusy(false);
    if (!r.ok) return say(errText(r.body), 'err');
    setDraft(null);
    // PM gaps B7: Min Masjid is told by the server (operator alert + the console's log); the toast says so.
    say(`${r.body.message_no || 'Publisert.'}${r.body.sentAt ? ` Sendt til Masjid ${ddmm(r.body.sentAt)} for godkjenning.` : ''}`, 'ok');
    reload();
  };
  const view = async (v: Body) => {
    try { const x = await call<Body>(`/privacy-page/versions/${v.id}`); setViewing({ id: x.id, title: x.title || '', html: x.html }); }
    catch (e) { say((e as Error).message, 'err'); }
  };
  const rollback = async (v: Body) => {
    setBusy(true);
    const r = await post(`/privacy-page/versions/${v.id}/rollback`, { confirm: true });
    setBusy(false);
    if (!r.ok) return say(errText(r.body), 'err');
    setViewing(null);
    say(`${r.body.message_no || 'Satt tilbake.'}${r.body.sentAt ? ` Sendt til Masjid ${ddmm(r.body.sentAt)} for godkjenning.` : ''}`, 'ok');
    reload();
  };

  const page = data.page as Body | null;
  const readiness = (data.readiness || []) as Body[];
  const inForce = readiness.filter((x) => x.ready);
  // PM gaps B7: the areas the mosque has (or would have, but for this page) first; the rest folded under «Andre områder».
  const wanted = readiness.filter((x) => x.wanted !== false);
  const others = readiness.filter((x) => x.wanted === false);
  const sentAt: string | null = data.review && data.review.sentAt ? data.review.sentAt : null;
  const scopeRow = (x: Body) => (
    <tr key={x.scope}>
      <td><strong>{(x.capabilities || []).map((c: Body) => c.label_no).join(', ')}</strong></td>
      <td><Tag tone={x.ready ? 'green' : x.reason === 'page_changed' ? 'gold' : 'grey'}>{x.ready ? 'godkjent' : x.reason === 'page_changed' ? 'på pause' : 'ikke godkjent'}</Tag>{' '}
        <span className="note">{x.status_no}{x.approvedAt ? ` Godkjent ${osloTime(x.approvedAt)}.` : ''}</span></td>
    </tr>);
  return (
    <>
      <Card title="Personvern" hint="Moskeens personvernerklæring, slik alle ser den. Moskeen laster opp siden; Masjid godkjenner den før funksjoner som lagrer opplysninger om ansatte eller påmeldte kan brukes.">
        <Msg text={msg.text} tone={msg.tone} />
        <div style={{ padding: '0 18px 14px' }}>
          <p>Adresse: <a href={data.liveUrl} target="_blank" rel="noopener noreferrer">{data.liveUrl}</a></p>
          <p>{MODE_NO[data.mode] || ''}{data.redirectTo ? ` (${data.redirectTo})` : ''}</p>
          {page && (
            <p className="field note">
              <strong>{page.title || 'Uten tittel'}</strong> · fingeravtrykk {short(page.sha256)} · {kb(page.bytes)} ·{' '}
              {page.inHistory && page.installedBy
                ? <>publisert {osloTime(page.installedAt)} av {page.installedBy.name}</>
                : <>lagt inn {osloTime(page.installedAt)}, før versjonshistorikken</>}
            </p>
          )}
        </div>
      </Card>

      <Card title="Godkjenning fra Masjid" hint="Masjid leser siden og godkjenner den for hvert område. Godkjenningen gjelder bare akkurat denne siden.">
        {sentAt && <p className="field note" style={{ margin: '0 18px 10px' }}><Tag tone="gold">Sendt til Masjid {ddmm(sentAt)}</Tag> Siden venter på godkjenning for områdene under. Du trenger ikke gjøre noe mer.</p>}
        {wanted.length > 0
          ? <table className="t">
            <thead><tr><th scope="col">Område</th><th scope="col">Status</th></tr></thead>
            <tbody>{wanted.map(scopeRow)}</tbody>
          </table>
          : <p className="note" style={{ padding: '0 18px 10px' }}>Moskeen har ingen funksjoner som venter på godkjenning av personvernsiden.</p>}
        {others.length > 0 && (
          <div className="card-body" style={{ paddingTop: 0 }}>
            <More label={`Andre områder (${others.length}) – ikke med i abonnementet`}>
              <table className="t">
                <thead><tr><th scope="col">Område</th><th scope="col">Status</th></tr></thead>
                <tbody>{others.map(scopeRow)}</tbody>
              </table>
            </More>
          </div>)}
      </Card>

      <Card title="Last opp ny versjon" hint={`Én HTML-fil med alt i seg (ingen skript, ingen eksterne filer utenom bilder), med en <title>. Høyst ${kb(data.maxBytes)}. Du ser siden før den publiseres.`}>
        <div style={{ padding: '0 18px 16px' }}>
          <DropZone title={draft ? 'Velg en annen fil' : 'Slipp HTML-filen her, eller klikk'} accept=".html,.htm,text/html" onFile={(f) => { void choose(f); }}>
            <span className="note" style={{ display: 'block', marginTop: 4 }}>Ingenting publiseres før du har sett siden og trykket «Publiser».</span>
          </DropZone>
          {checking && <p className="note" role="status">Sjekker filen …</p>}
          {draft && (
            <div style={{ marginTop: 12 }}>
              <p className="field note"><strong>{draft.title}</strong> · {draft.name} · {kb(draft.bytes)} · fingeravtrykk {short(draft.sha256)}</p>
              {draft.pauses.length > 0 && <p className="warnbox" role="alert">{pauseWarning(labelsOf(draft.pauses))}</p>}
              <Preview html={draft.html} title={`Forhåndsvisning: ${draft.title}`} />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <TwoStep label="Publiser" confirmLabel="Publiser — trykk igjen" ghost={false} disabled={busy} onConfirm={publish} />
                <Btn ghost disabled={busy} onClick={() => { setDraft(null); say(''); }}>Avbryt</Btn>
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card title="Versjoner" hint="Hver publisering, hvem som gjorde den og når. En tidligere versjon kan vises og settes tilbake.">
        {inForce.length > 0 && (data.versions || []).length > 1 && <p className="field note" style={{ margin: '0 18px 10px' }}>Settes en annen versjon tilbake: {pauseWarning(labelsOf(inForce))}</p>}
        {(data.versions || []).length === 0
          ? <p className="note" style={{ padding: '0 18px 14px' }}>Ingen versjoner ennå.</p>
          : <table className="t">
            <thead><tr><th scope="col">Når</th><th scope="col">Hvem</th><th scope="col">Side</th><th scope="col"><span className="sr-only">Handling</span></th></tr></thead>
            <tbody>{(data.versions as Body[]).map((v) => (
              <tr key={v.id}>
                <td>{osloTime(v.at)}<span className="note" style={{ display: 'block' }}>{ACTION_NO[v.action] || v.action}{v.fromVersion ? ` (versjon ${v.fromVersion})` : ''}</span></td>
                <td>{v.by ? v.by.name : ''}</td>
                <td>{v.title || 'Uten tittel'}<span className="note" style={{ display: 'block' }}>{short(v.sha256)} · {kb(v.bytes)}{v.current ? ' · vises nå' : ''}</span></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Btn ghost onClick={() => { void view(v); }}>Vis</Btn>{' '}
                  {!v.current && <TwoStep label="Sett tilbake" confirmLabel="Sett tilbake — trykk igjen" disabled={busy} onConfirm={() => rollback(v)} />}
                </td>
              </tr>))}</tbody>
          </table>}
        {viewing && (
          <div style={{ padding: '0 18px 16px' }}>
            <p className="field note">Versjon {viewing.id}: <strong>{viewing.title || 'Uten tittel'}</strong>{' '}<Btn ghost onClick={() => setViewing(null)}>Lukk</Btn></p>
            <Preview html={viewing.html} title={`Versjon ${viewing.id}: ${viewing.title}`} />
          </div>
        )}
      </Card>
    </>
  );
}
